/**
 * Реестр целей Яндекс.Метрики.
 *
 * ⚠️ Зачем он вообще нужен. У Метрики нет произвольных событий: есть цели
 * (жёсткий лимит 200 на счётчик) и параметры визитов (без лимита). У нас 611
 * имён событий, поэтому каждый track() уходит двумя слоями — в параметры
 * визита всегда, а reachGoal только для имён из этого файла.
 *
 * Что даёт цель, чего не даёт параметр:
 *   — конверсии и воронки;
 *   — **фильтр записей Вебвизора**. Запись, не помеченную целью, в интерфейсе
 *     не найти. Поэтому в реестре не только успехи, но и провалы: именно на
 *     них хочется смотреть глазами, а не читать агрегат.
 *
 * Правило пополнения: цель заводится, если ответ на неё меняет решение, либо
 * если по ней придётся искать запись сессии. Всё остальное — микромеханика
 * (`*_opened`, `*_toggled`, `*_viewed`) — живёт в параметрах визитов и Logs API
 * и в реестр не попадает.
 *
 * ⚠️ Идентификатор цели в Метрике = имя события здесь, буква в букву. Второй
 * словарь не заводим: любая перекодировка разъедется через полгода.
 *
 * ⚠️ Лимит общий на весь домен: счётчик один на лендинг, app. и поддомены
 * тенантов. Под GTM-цели лендинга («заявка», «клик по тарифу») зарезервировано
 * ~20 позиций — их заводить сюда же, а не отдельным списком.
 *
 * Сейчас: 45 из 200 (44 продукт + 1 лендинг).
 *
 * Полный разбор — metrika_migration_plan.md, раздел 6.
 */

// title — название цели в интерфейсе Метрики. Русское и человекочитаемое:
// отчёт смотрят глазами, и `smart_camera_discarded` в списке из сорока строк
// ничего не говорит даже автору через месяц.
export const GOALS = [
  // ── Активация ──────────────────────────────────────────────────────────────
  { id: 'register_completed',          title: 'Регистрация завершена' },
  { id: 'onboarding_setup_completed',  title: 'Организация настроена' },
  { id: 'onboarding_checklist_all_done', title: 'Чек-лист онбординга пройден' },
  { id: 'onboarding_dns_timeout',      title: '⚠️ Домен не поднялся' },
  { id: 'demo_started',                title: 'Демо начато' },
  { id: 'demo_tour_completed',         title: 'Тур демо пройден' },
  { id: 'invite_member',               title: 'Приглашён сотрудник' },
  { id: 'team_invite_resent',          title: 'Приглашение отправлено повторно' },
  { id: 'team_invite_link_copied',     title: 'Ссылка приглашения скопирована' },

  // ── Ядро продукта ──────────────────────────────────────────────────────────
  { id: 'task_created',                title: 'Работа создана' },
  { id: 'property_created',            title: 'Объект создан' },
  { id: 'client_created',              title: 'Контакт создан' },
  { id: 'photo_uploaded',              title: 'Фото загружено' },
  { id: 'report_generated',            title: 'Отчёт сформирован' },
  { id: 'report_shared',               title: 'Отчёт отправлен клиенту' },
  { id: 'custom_report_failed',        title: '⚠️ Генерация отчёта упала' },
  { id: 'video_upload_failed',         title: '⚠️ Видео не загрузилось' },

  // ── Деньги ─────────────────────────────────────────────────────────────────
  { id: 'quote_created',               title: 'Смета создана' },
  { id: 'quote_approved_by_admin',     title: 'Смета согласована' },
  { id: 'quote_email_sent',            title: 'Смета отправлена клиенту' },
  { id: 'quote_invoice_issued',        title: 'По смете выставлен счёт' },
  { id: 'zoho_invoice_created',        title: 'Счёт создан в Zoho' },
  { id: 'zoho_invoice_emailed',        title: 'Счёт отправлен клиенту' },
  { id: 'zoho_invoice_error',          title: '⚠️ Ошибка Zoho' },
  { id: 'income_draft_saved',          title: 'Начисление сохранено' },
  { id: 'work_fact_submit',            title: 'Факт исполнения проведён' },
  { id: 'overrun_quote_created',       title: 'Перерасход превращён в смету' },

  // ── Инспекции и камера ─────────────────────────────────────────────────────
  { id: 'inspection_report_built',     title: 'Отчёт инспекции собран' },
  { id: 'inspection_repair_created',   title: 'Из находки создана Работа' },
  { id: 'smart_camera_session_started', title: 'Съёмка начата' },
  { id: 'smart_camera_confirmed',      title: 'Съёмка подтверждена' },
  { id: 'smart_camera_discarded',      title: '⚠️ Съёмка брошена' },
  { id: 'smart_camera_upload_failed',  title: '⚠️ Фото с объекта не ушло' },

  // ── Оборудование, пермиты, прайс ───────────────────────────────────────────
  { id: 'equipment_created',           title: 'Оборудование заведено' },
  { id: 'equipment_scan',              title: 'QR отсканирован' },
  { id: 'ptw_request_confirmed_sent',  title: 'Пермит отправлен в УК' },
  { id: 'price_item_created',          title: 'Позиция прайса создана' },
  { id: 'price_import_committed',      title: 'Прайс импортирован' },

  // ── Удержание и вовлечение ─────────────────────────────────────────────────
  { id: 'user_login',                  title: 'Вход в систему' },
  { id: 'worker_started_task',         title: 'Работник приступил к Работе' },
  { id: 'advisor_card_clicked',        title: 'Клик по карточке советника' },
  { id: 'worker_advisor_card_clicked', title: 'Клик по карточке советника (Работник)' },
  { id: 'notif_clicked',               title: 'Клик по уведомлению' },
  { id: 'support_question_sent',       title: 'Вопрос в поддержку' },
  { id: 'support_feedback_given',      title: 'Оценка ответа поддержки' },
  { id: 'home_screen_install_result',  title: 'PWA установлено' },

  // ── Лендинг vsenapole.ru (Tilda) ───────────────────────────────────────────
  // Лендинг живёт на Tilda, не в репо: reachGoal шлёт сниппет из «Настройки
  // сайта → Вставка кода», его текст — landing_vsenapole_tilda.md. Имя цели
  // здесь и в сниппете — буква в букву.
  { id: 'landing_presentation_downloaded', title: 'Лендинг: скачана презентация' },
];

// O(1) на каждый track() — их тысячи за сессию, линейный поиск по массиву
// здесь был бы бессмысленной работой в горячем пути.
const GOAL_IDS = new Set(GOALS.map((g) => g.id));

export function isGoal(event) {
  return GOAL_IDS.has(event);
}

export const GOALS_LIMIT = 200;
